import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function SortHeader({ label, column, sortColumn, sortDirection, onSort }) {
  const isActive = sortColumn === column
  return (
    <div
      className={`sortable-header${isActive ? ' sort-active' : ''}`}
      onClick={() => onSort(column)}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="sort-indicator">{isActive ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ↕'}</span>
    </div>
  )
}

function Forums() {
  const { user } = useAuth()
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)

  // Filters
  const [filterTopic, setFilterTopic] = useState('')
  const [filterLure, setFilterLure] = useState('')
  const [filterUser, setFilterUser] = useState('')

  // Sort
  const [sortColumn, setSortColumn] = useState('updated_at')
  const [sortDirection, setSortDirection] = useState('desc')

  useEffect(() => {
    fetchThreads()
  }, [])

  const fetchThreads = async () => {
    try {
      const { data: threadData, error } = await supabase
        .from('forum_threads')
        .select(`
          thread_id, title, created_at, updated_at, user_id, is_locked,
          base_lures(base_lure_id, name, brands(brand_name)),
          forum_posts(post_id)
        `)
        .order('updated_at', { ascending: false })

      if (error) throw error

      const userIds = [...new Set((threadData || []).map(t => t.user_id))]
      const { data: usersData } = userIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', userIds)
        : { data: [] }

      const userMap = {}
      usersData?.forEach(u => { userMap[u.id] = u.username })

      setThreads((threadData || []).map(t => ({
        ...t,
        username: userMap[t.user_id] || 'Unknown',
        postCount: t.forum_posts?.length || 0,
        lureName: t.base_lures?.name || '',
        brandName: t.base_lures?.brands?.brand_name || '',
      })))
    } catch (err) {
      console.error('Error fetching forums:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection(column === 'updated_at' || column === 'postCount' ? 'desc' : 'asc')
    }
  }

  const displayedThreads = useMemo(() => {
    let result = threads

    if (filterTopic.trim()) {
      const q = filterTopic.toLowerCase()
      result = result.filter(t => t.title.toLowerCase().includes(q))
    }
    if (filterLure.trim()) {
      const q = filterLure.toLowerCase()
      result = result.filter(t =>
        t.lureName.toLowerCase().includes(q) || t.brandName.toLowerCase().includes(q)
      )
    }
    if (filterUser.trim()) {
      const q = filterUser.toLowerCase()
      result = result.filter(t => t.username.toLowerCase().includes(q))
    }

    result = [...result].sort((a, b) => {
      let aVal, bVal
      switch (sortColumn) {
        case 'title':    aVal = a.title.toLowerCase();    bVal = b.title.toLowerCase(); break
        case 'lureName': aVal = a.lureName.toLowerCase(); bVal = b.lureName.toLowerCase(); break
        case 'username': aVal = a.username.toLowerCase(); bVal = b.username.toLowerCase(); break
        case 'postCount': aVal = a.postCount; bVal = b.postCount; break
        case 'updated_at': aVal = new Date(a.updated_at); bVal = new Date(b.updated_at); break
        default: return 0
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return result
  }, [threads, filterTopic, filterLure, filterUser, sortColumn, sortDirection])

  if (loading) return <div className="loading">Loading forums...</div>

  return (
    <div className="forum-page">
      <div className="forum-page-header">
        <div>
          <h1>Forums</h1>
          <p className="page-subtitle">All discussions across the lure database</p>
        </div>
      </div>

      {/* Filters */}
      <div className="collection-filters" style={{ marginBottom: '1rem' }}>
        <div className="collection-filters-row">
          <input
            type="text"
            className="filter-input"
            placeholder="Filter by topic..."
            value={filterTopic}
            onChange={e => setFilterTopic(e.target.value)}
          />
          <input
            type="text"
            className="filter-input"
            placeholder="Filter by lure or brand..."
            value={filterLure}
            onChange={e => setFilterLure(e.target.value)}
          />
          <input
            type="text"
            className="filter-input filter-input-narrow"
            placeholder="Filter by user..."
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
          />
          {(filterTopic || filterLure || filterUser) && (
            <button
              className="clear-sort-btn"
              onClick={() => { setFilterTopic(''); setFilterLure(''); setFilterUser('') }}
            >
              Clear Filters
            </button>
          )}
        </div>
        <div className="collection-results-count">
          {displayedThreads.length} of {threads.length} thread{threads.length !== 1 ? 's' : ''}
        </div>
      </div>

      {threads.length === 0 ? (
        <div className="no-content-box">
          <p>No discussions yet. Browse a lure and start the first one!</p>
          <Link to="/browse" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
            Browse Lures
          </Link>
        </div>
      ) : displayedThreads.length === 0 ? (
        <div className="no-content-box">
          <p>No threads match your filters.</p>
        </div>
      ) : (
        <div className="forum-thread-list-full">
          <div className="forum-thread-list-header forums-global-header">
            <SortHeader label="Topic"         column="title"      sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
            <SortHeader label="Lure"          column="lureName"   sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
            <SortHeader label="Started by"    column="username"   sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
            <SortHeader label="Posts"         column="postCount"  sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
            <SortHeader label="Last Activity" column="updated_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
          </div>

          {displayedThreads.map(thread => (
            <Link
              key={thread.thread_id}
              to={`/lure/${thread.base_lures?.base_lure_id}/discussions/${thread.thread_id}`}
              className="forum-thread-list-row forums-global-row"
            >
              <div>
                <span className="ftl-thread-title">{thread.title}</span>
                {thread.is_locked && <span className="ftl-locked-badge">🔒</span>}
              </div>
              <div className="ftl-lure-name">
                {thread.lureName}
                {thread.brandName && <span className="ftl-brand"> · {thread.brandName}</span>}
              </div>
              <div className="ftl-meta">{thread.username}</div>
              <div className="ftl-posts">{thread.postCount}</div>
              <div className="ftl-activity">{new Date(thread.updated_at).toLocaleDateString()}</div>
            </Link>
          ))}
        </div>
      )}

      {user && (
        <p className="forum-login-note" style={{ marginTop: '1.5rem' }}>
          To start a discussion, browse to a lure and click "Start a Discussion" on its page.
        </p>
      )}
      {!user && (
        <p className="forum-login-note" style={{ marginTop: '1.5rem' }}>
          <Link to="/login">Log in</Link> to join the conversation. Browse a lure to start a discussion.
        </p>
      )}
    </div>
  )
}

export default Forums
